<script setup lang="ts">
import type { NuxtError } from 'nuxt/app';
import type { EngineError } from '#layers/base/types/error';

import { useOverlay } from '@nuxt/ui/composables';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '#app/composables/head';
import { getErrorStatusCode } from '#layers/base';
import { hasErrorCode, isEngineError, isEnginePanicError } from '#layers/base/utils/error';
import { useTranslationUtils } from '#layers/i18n';
import { useErrorFormatter } from '~/composables/use-error-formatter';

const props = defineProps<{
	error?: Error | NuxtError & { statusCode?: number; statusMessage?: string } | EngineError;
	clearError?: () => void;
}>();

const { t } = useI18n();
const { translateString } = useTranslationUtils();
const { formatError } = useErrorFormatter();
const { showDetails } = useShowDetails();
const showClear = computed(() => import.meta.dev && !!props.clearError);

const statusCode = computed(() => getErrorStatusCode(props.error));
const isProjectAccessDenied = computed(() => props.error && hasErrorCode(props.error, 'PROJECT_ACCESS_DENIED'));

const title = computed(() => {
	if (!props.error) {
		return null;
	}
	else if (isEnginePanicError(props.error)) {
		return 'Engine Panic';
	}
	else if ('statusMessage' in props.error && props.error.statusMessage) {
		return translateString(props.error.statusMessage);
	}
	else if (isProjectAccessDenied.value) {
		return t('project.no_access');
	}
	else if (statusCode.value === 404) {
		return t('errors.page_not_found');
	}
	else {
		return formatError(props.error);
	}
});

const description = computed(() => {
	if (!props.error) {
		return null;
	}

	if (isProjectAccessDenied.value) {
		return t('project.no_access_description');
	}

	if (statusCode.value === 404 && !('message' in props.error && props.error.message)) {
		return t('errors.page_not_found_description');
	}

	const formattedMessage = formatError(props.error);

	// if the error has a message that is different from the title, render the message
	if (formattedMessage !== title.value) {
		return formattedMessage;
	}

	return null;
});

const LazyIllustration404 = defineAsyncComponent(() => import('~/assets/img/illustrations/404.svg'));
const LazyIllustrationError = defineAsyncComponent(() => import('~/assets/img/illustrations/error.svg'));
const LazyIllustrationPanic = defineAsyncComponent(() => import('~/assets/img/illustrations/panic.svg'));
const LazyErrorDetailDialog = defineAsyncComponent(() => import('~/components/Error/ErrorDetailDialog.vue'));

useHead({
	title,
});

function useShowDetails() {
	const overlay = useOverlay();

	return {
		showDetails,
	};

	function showDetails() {
		overlay.create(LazyErrorDetailDialog, {
			props: {
				error: props.error,
			},
		}).open();
	}
}
</script>

<template>
	<div class="flex h-full flex-col items-center justify-center gap-2" data-test-id="error-screen">
		<template v-if="isProjectAccessDenied || statusCode === 404">
			<LazyIllustration404 class="w-32" />
		</template>
		<template v-else-if="isEnginePanicError(error)">
			<LazyIllustrationPanic class="w-32" />
		</template>
		<template v-else>
			<LazyIllustrationError class="w-32" />
		</template>

		<div class="max-w-100 space-y-1 text-center text-sm">
			<h1 class="font-medium text-default">
				{{ title }}
			</h1>
			<p class="text-muted">
				{{ description }}
			</p>
			<DButton v-if="isEngineError(error)" variant="link" color="neutral" :label="$t('common.view_details')" @click="showDetails" />
		</div>

		<div v-if="showClear" class="mt-20">
			<DButton color="neutral" @click="clearError">
				Clear Error
			</DButton>
		</div>

		<div v-if="!!$slots.default" class="mt-20">
			<slot />
		</div>
	</div>
</template>
